// Ported from compose_v2's AppSettingsPage, restyled with WYRM's Tailwind classes.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { fetchAppSettings, resetAppSetting, updateAppSettings } from '../services/admin';

const serializeEditable = (setting) => {
  if (setting?.type === 'boolean') return Boolean(setting.value);
  return setting?.value ?? '';
};

const parseEditable = (setting, value) => {
  if (setting.type === 'boolean') return Boolean(value);
  if (setting.type === 'integer') {
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) throw new Error('Enter a whole number');
    if (setting.minimum !== null && setting.minimum !== undefined && parsed < setting.minimum) {
      throw new Error(`Minimum is ${setting.minimum}`);
    }
    if (setting.maximum !== null && setting.maximum !== undefined && parsed > setting.maximum) {
      throw new Error(`Maximum is ${setting.maximum}`);
    }
    return parsed;
  }
  if (setting.type === 'number') {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) throw new Error('Enter a number');
    return parsed;
  }
  return String(value ?? '');
};

const settingEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
};

const BADGE_TONES = {
  db: 'bg-blue-100 text-blue-800',
  env: 'bg-gray-100 text-gray-700',
  default: 'bg-gray-100 text-gray-500',
  dirty: 'bg-amber-100 text-amber-800',
  admin: 'bg-red-100 text-red-800',
};

const Badge = ({ tone = 'default', title, children }) => (
  <span title={title} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${BADGE_TONES[tone] || BADGE_TONES.default}`}>
    {children}
  </span>
);

const SOURCE_LABELS = {
  db: ['Set here', 'Saved from this page. Reset returns it to the .env or default value.'],
  env: ['From .env', 'Read from the root .env file.'],
  default: ['Default', 'Built-in default.'],
};

const OPTIONS_SOURCE_LABELS = {
  manual: 'Manual deployed model list',
  management_api: 'Discovered from Azure',
  static_registry: 'Static model list',
};

const DEPLOYMENT_SOURCE_LABELS = {
  override: 'deployment override',
  deployment_map: 'deployment map',
  discovered: 'discovered deployment',
  model_name: 'model name',
};

const settingMatches = (setting, needle) =>
  [setting.label, setting.key, setting.description].some((text) => String(text || '').toLowerCase().includes(needle));

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const OptionsSourceHint = ({ optionsSource, value }) => {
  if (!optionsSource) return null;
  const resolved = optionsSource.deploymentsByModel?.[value];
  return (
    <div className="mt-2 space-y-1 text-xs text-gray-600">
      <Badge tone={optionsSource.source === 'static_registry' ? 'default' : 'db'}>
        {OPTIONS_SOURCE_LABELS[optionsSource.source] || optionsSource.source}
      </Badge>
      {resolved ? (
        <p>
          Deployment <strong>{resolved.deployment}</strong> ({DEPLOYMENT_SOURCE_LABELS[resolved.source] || resolved.source})
          {resolved.tokensPerMinute !== null && resolved.tokensPerMinute !== undefined
            ? ` · ${Number(resolved.tokensPerMinute).toLocaleString('en-GB')} tokens/min`
            : ''}
          {resolved.sku ? ` · ${resolved.sku}` : ''}
          {resolved.warning ? <> <Badge tone="dirty">low capacity</Badge></> : null}
          {optionsSource.source !== 'static_registry' && !resolved.found ? <> <Badge tone="admin">not found on resource</Badge></> : null}
        </p>
      ) : null}
      {optionsSource.error ? <p className="text-red-600">{optionsSource.error}</p> : null}
    </div>
  );
};

const SettingInput = ({ setting, value, onChange, id }) => {
  if (setting.type === 'boolean') {
    return <input id={id} type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />;
  }
  if (setting.type === 'enum') {
    const options = setting.options || [];
    // Keep a saved value visible even when it is no longer in the option list.
    const missingValue = value && !options.includes(value) ? value : null;
    return (
      <select id={id} className={inputClass} value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        {missingValue ? <option value={missingValue}>{`${missingValue} (not in list)`}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={id}
      className={inputClass}
      type={setting.type === 'integer' || setting.type === 'number' ? 'number' : 'text'}
      min={setting.minimum ?? undefined}
      max={setting.maximum ?? undefined}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

const AppSettingsPage = () => {
  const [payload, setPayload] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setPayload(await fetchAppSettings());
      setDrafts({});
      setErrors({});
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allSettings = useMemo(
    () => (payload?.groups || []).flatMap((group) => group.settings || []),
    [payload],
  );
  const settingsByKey = useMemo(() => new Map(allSettings.map((setting) => [setting.key, setting])), [allSettings]);
  const dirtyEntries = Object.entries(drafts).filter(([key, value]) => {
    const setting = settingsByKey.get(key);
    return setting && !settingEqual(value, serializeEditable(setting));
  });
  const hasChanges = dirtyEntries.length > 0;
  const needle = query.trim().toLowerCase();
  const shownSettings = needle ? allSettings.filter((setting) => settingMatches(setting, needle)) : allSettings;

  const save = async () => {
    const nextErrors = {};
    const updates = {};
    dirtyEntries.forEach(([key, value]) => {
      try {
        updates[key] = parseEditable(settingsByKey.get(key), value);
      } catch (error) {
        nextErrors[key] = error.message;
      }
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error('Fix validation errors before saving');
      return;
    }
    setSaving(true);
    try {
      setPayload(await updateAppSettings(updates));
      setDrafts({});
      toast.success('Settings saved');
    } catch (error) {
      setErrors(error?.response?.data?.detail?.errors || {});
      toast.error(errorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const resetSetting = async (setting) => {
    setSaving(true);
    try {
      setPayload(await resetAppSetting(setting.key));
      setDrafts((current) => {
        const next = { ...current };
        delete next[setting.key];
        return next;
      });
      toast.success(`${setting.label} reset`);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to reset setting'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-gray-600">Loading settings...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">
            Runtime settings for the meeting LLM, stored in the database. Secrets and startup wiring stay in{' '}
            <code className="font-mono bg-gray-100 px-1 rounded">.env</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {payload?.summary?.db_override_count || 0} set here · {payload?.summary?.setting_count || 0} settings
          </span>
          <button
            type="button"
            onClick={load}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!hasChanges || saving}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : `Save${hasChanges ? ` (${dirtyEntries.length})` : ''}`}
          </button>
        </div>
      </div>

      <label className="block mb-4">
        <span className="sr-only">Find a setting</span>
        <input
          type="search"
          className={inputClass}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Find a setting, such as model, cost or anthropic"
        />
      </label>

      {shownSettings.length ? (
        <div className="space-y-4">
          {shownSettings.map((setting) => {
            const editableValue = Object.prototype.hasOwnProperty.call(drafts, setting.key)
              ? drafts[setting.key]
              : serializeEditable(setting);
            const isDirty = !settingEqual(editableValue, serializeEditable(setting));
            const [sourceLabel, sourceTitle] = SOURCE_LABELS[setting.source] || [setting.source, ''];
            const inputId = `setting-${setting.key}`;
            return (
              <article key={setting.key} className="bg-white rounded-lg shadow-md p-5 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <label htmlFor={inputId} className="text-lg font-semibold text-gray-900">{setting.label}</label>
                    <Badge tone={setting.source} title={sourceTitle}>{sourceLabel}</Badge>
                    {isDirty ? <Badge tone="dirty">edited</Badge> : null}
                    {setting.dangerous ? <Badge tone="admin">admin review</Badge> : null}
                  </div>
                  <p className="text-sm text-gray-600">{setting.description}</p>
                  <code className="text-xs text-gray-400">{setting.key}</code>
                </div>
                <div>
                  <SettingInput
                    id={inputId}
                    setting={setting}
                    value={editableValue}
                    onChange={(value) => {
                      setDrafts((current) => ({ ...current, [setting.key]: value }));
                      setErrors((current) => ({ ...current, [setting.key]: '' }));
                    }}
                  />
                  <OptionsSourceHint optionsSource={setting.optionsSource} value={editableValue} />
                  {errors[setting.key] ? <p className="mt-1 text-sm text-red-600">{errors[setting.key]}</p> : null}
                  {setting.source === 'db' ? (
                    <button
                      type="button"
                      onClick={() => resetSetting(setting)}
                      disabled={saving}
                      className="mt-2 text-sm text-blue-600 hover:underline disabled:opacity-50"
                    >
                      Reset
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600">No settings match "{query.trim()}".</p>
      )}
    </div>
  );
};

export default AppSettingsPage;
